import {
  LISTING_FIELDS,
  REQUIRED_LISTING_FIELDS,
  type ListingFieldKey,
  type MappedListingRow,
} from '../../types/vehicleListing.js';

// SMS Lead Bot Phase 1 — CSV column mapping. PURE.
//
// ⚠️ MAPPING RATHER THAN FIXED HEADERS, because feeds differ per client. The
// bulk-contact-upload flow matches fixed header labels and rejects a file that
// does not use them; that is right for a template we hand out, and wrong for a
// file a client's DMS exports. So the auto-match here is a SUGGESTION the AM can
// override, in the editable-dropdown shape the onboarding mapping table already
// uses.
//
// ⚠️ AN UNMAPPABLE REQUIRED COLUMN IS REPORTED, NEVER GUESSED. Picking the
// "closest" header for a required field is how a description column ends up
// holding the stock id and every listing is silently wrong.

export type ColumnMapping = Partial<Record<ListingFieldKey, string>>;

function normalizeHeader(value: string): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .replace(/[^a-z0-9 ]/g, '')
    .trim();
}

/**
 * Best-guess mapping from the file's headers onto our fields.
 *
 * ⚠️ EXACT-ISH ONLY. It matches a normalised header against the field's label
 * or one of its known aliases — no fuzzy scoring, no "closest match". A field
 * with no confident match is left UNMAPPED for the AM to set, because an
 * unmapped column is visible and a wrongly-mapped one is not.
 */
export function suggestColumnMapping(headers: readonly string[]): ColumnMapping {
  const byNormalized = new Map<string, string>();
  for (const header of headers) {
    const key = normalizeHeader(header);
    if (key !== '' && !byNormalized.has(key)) byNormalized.set(key, header);
  }

  const mapping: ColumnMapping = {};
  const taken = new Set<string>();
  for (const field of LISTING_FIELDS) {
    const candidates = [field.label, field.key, ...field.aliases].map(normalizeHeader);
    for (const candidate of candidates) {
      const header = byNormalized.get(candidate);
      if (header !== undefined && !taken.has(header)) {
        mapping[field.key] = header;
        taken.add(header);
        break;
      }
    }
  }
  return mapping;
}

/** Required fields the mapping does not yet cover, by friendly label. */
export function missingRequiredFields(mapping: ColumnMapping): string[] {
  return REQUIRED_LISTING_FIELDS.filter((key) => {
    const header = mapping[key];
    return header === undefined || header.trim() === '';
  }).map((key) => LISTING_FIELDS.find((f) => f.key === key)?.label ?? key);
}

export function isMappingComplete(mapping: ColumnMapping): boolean {
  return missingRequiredFields(mapping).length === 0;
}

/**
 * Apply the mapping to the parsed rows. Rows where every mapped value is blank
 * are dropped — a trailing empty line in a CSV is not a listing.
 */
export function applyMapping(
  mapping: ColumnMapping,
  records: ReadonlyArray<Record<string, unknown>>,
): MappedListingRow[] {
  const entries = Object.entries(mapping).filter(
    (entry): entry is [ListingFieldKey, string] =>
      typeof entry[1] === 'string' && entry[1].trim() !== '',
  );

  const rows: MappedListingRow[] = [];
  for (const record of records) {
    const row: MappedListingRow = {};
    let hasValue = false;
    for (const [field, header] of entries) {
      const raw = record[header];
      const value = raw === null || raw === undefined ? '' : String(raw).trim();
      row[field] = value;
      if (value !== '') hasValue = true;
    }
    if (hasValue) rows.push(row);
  }
  return rows;
}

/** Header options for a mapping dropdown — every column in the file, plus "not mapped". */
export function headerOptions(headers: readonly string[]): Array<{ value: string; label: string }> {
  return [
    { value: '', label: '— not mapped —' },
    ...headers
      .filter((h) => (h ?? '').trim() !== '')
      .map((h) => ({ value: h, label: h })),
  ];
}
